import {
    // TextureLoader,
    // CubeTextureLoader,
    Vector3,
    MeshBasicMaterial,
    Mesh,
    Color,
    DoubleSide,
    ShapeGeometry,
    Group,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
// import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import EventEmitterSingleton from './EventEmitter.js';
import Events from './Events.js';

const ARTWORKS_API = 'https://premiodrivingenergy.terna.it/wp-json/driving/v1/selected';

export default class Resources {
    constructor(resources) {
        // Options
        this.resources = resources;

        // Setup
        this.environments = [];
        this.items = [];
        this.toLoad = this.resources.environments.length + this.resources.items.length;
        this.loaded = 0;

        // Event handling
        this.eventEmitter = new EventEmitterSingleton();

        this.setLoaders();
        this.startLoading();
    }

    setLoaders() {
        this.loaders = {};
        this.loaders.dracoLoader = new DRACOLoader();
        this.loaders.dracoLoader.setDecoderPath('./draco/');
        this.loaders.gltfLoader = new GLTFLoader();
        this.loaders.gltfLoader.setDRACOLoader(this.loaders.dracoLoader);
    }

    startLoading() {
        // Load each resource
        for (const resource of this.resources.environments) {
            switch (resource.type) {
                case 'gltfModel':
                    this.loaders.gltfLoader.load(resource.path, (file) => {
                        if (file.scene) {
                            this.setModelLayer(file.scene, 1);
                        }
                        this.sourceLoaded(resource, file, this.environments);
                    });
                    break;
                case 'cubeTexture':
                    this.loaders.cubeTextureLoader
                        .setPath(resource.path)
                        .load(
                            ['px.jpg', 'nx.jpg', 'py.jpg', 'ny.jpg', 'pz.jpg', 'nz.jpg'],
                            (file) => {
                                this.sourceLoaded(resource, file, this.environments);
                            }
                        );
                    break;
                case 'svg':
                    this.loaders.svgLoader.load(resource.path, (data) => {
                        const file = this.buildSvgObject(data);
                        this.sourceLoaded(resource, file, this.environments);
                    });
                    break;
                default:
                    continue;
            }
        }
        for (const resource of this.resources.items) {
            this.loaders.gltfLoader.load(resource.path, (file) => {
                this.sourceLoaded(resource, file, this.items);
            });
        }
    }

    sourceLoaded(resource, file, destination) {
        destination[resource.name] = { resource, file };
        this.loaded += 1;
        if (this.loaded === this.toLoad) {
            this.eventEmitter.trigger(Events.RESOURCES_READY, [this]);
        }
    }

    // N.B.: Currently not used
    unpackItemsData(data) {
        if (!(data instanceof Array)) {
            console.error('Data is not an array');
            return;
        }

        if (data.length != this.resources.items.length) {
            console.warn('Data length does not match resources length');
        }

        for (const remoteItem of data) {
            const localItem = this.resources.items.find(
                (item) => item.id === remoteItem.id
            );
            if (localItem) {
                localItem.name = remoteItem.name;
                localItem.title = remoteItem.title;
                localItem.description = remoteItem.description;
                localItem.images = remoteItem.images;
            }
        }
    }

    // N.B.: Currently not used
    buildSvgObject(shapePathData) {
        const paths = shapePathData.paths;

        const group = new Group();

        for (let i = 0; i < paths.length; i++) {
            const path = paths[i];

            const fillColor = path.userData.style.fill;
            if (fillColor !== undefined && fillColor !== 'none') {
                const material = new MeshBasicMaterial({
                    color: new Color().setStyle(fillColor).convertSRGBToLinear(),
                    opacity: path.userData.style.fillOpacity,
                    transparent: true,
                    side: DoubleSide,
                    depthWrite: false,
                });

                const shapes = SVGLoader.createShapes(path);

                for (let j = 0; j < shapes.length; j++) {
                    const shape = shapes[j];

                    const geometry = new ShapeGeometry(shape);
                    const mesh = new Mesh(geometry, material);

                    group.add(mesh);
                }
            }

            const strokeColor = path.userData.style.stroke;

            if (strokeColor !== undefined && strokeColor !== 'none') {
                const material = new MeshBasicMaterial({
                    color: new Color().setStyle(strokeColor).convertSRGBToLinear(),
                    opacity: path.userData.style.strokeOpacity,
                    transparent: true,
                    side: DoubleSide,
                    depthWrite: false,
                });

                for (let j = 0, jl = path.subPaths.length; j < jl; j++) {
                    const subPath = path.subPaths[j];

                    const geometry = SVGLoader.pointsToStroke(
                        subPath.getPoints(),
                        path.userData.style
                    );

                    if (geometry) {
                        const mesh = new Mesh(geometry, material);

                        group.add(mesh);
                    }
                }
            }
        }

        return group;
    }

    setModelLayer(model, layer) {
        model.layers.enable(layer);
        model.traverse((child) => {
            if (child.isMesh) {
                child.layers.enable(layer);
            }
        });
    }
}
